import * as Path from 'clownface-shacl-path';
import $rdf from '@zazuko/env/web.js';
import { emptyPatterns, flatten } from './shapePatterns.js';
export default class extends Path.PathVisitor {
    constructor(variable) {
        super();
        this.variable = variable;
    }
    visitAlternativePath({ paths }, { pathStart, pathEnd = this.variable() }) {
        let result = emptyPatterns;
        for (const path of paths) {
            const intermediatePath = this.variable();
            const inner = path.accept(this, { pathStart, pathEnd: intermediatePath });
            const whereClause = [
                ...inner.whereClause,
                {
                    type: 'bind',
                    variable: pathEnd,
                    expression: intermediatePath,
                },
            ];
            if (result === emptyPatterns) {
                result = {
                    whereClause,
                    constructClause: inner.constructClause,
                };
            }
            else if (result.whereClause.length === 1 && result.whereClause[0].type === 'union') {
                const union = result.whereClause[0];
                union.patterns.push({
                    type: 'group',
                    patterns: whereClause,
                });
                result.constructClause.push(...inner.constructClause);
            }
            else {
                result = {
                    whereClause: [{
                            type: 'union',
                            patterns: [{
                                    type: 'group',
                                    patterns: result.whereClause,
                                }, {
                                    type: 'group',
                                    patterns: whereClause,
                                }],
                        }],
                    constructClause: [...result.constructClause, ...inner.constructClause],
                };
            }
        }
        return result;
    }
    visitInversePath({ path }, { pathStart, pathEnd = this.variable() }) {
        return path.accept(this, { pathStart: pathEnd, pathEnd: pathStart });
    }
    visitOneOrMorePath(path, arg) {
        return this.greedyPath(path, arg);
    }
    visitPredicatePath(path, { pathStart, pathEnd = this.variable() }) {
        return {
            whereClause: [{
                    type: 'bgp',
                    triples: [{
                            subject: pathStart,
                            predicate: path.term,
                            object: pathEnd,
                        }],
                }],
            constructClause: [$rdf.quad(pathStart, path.term, pathEnd)],
        };
    }
    visitSequencePath({ paths }, { pathStart, pathEnd = this.variable() }) {
        let segStart = pathStart;
        let segEnd = this.variable();
        let result = emptyPatterns;
        for (const [index, segment] of paths.entries()) {
            const isLast = index === paths.length - 1;
            result = flatten(result, segment.accept(this, {
                pathStart: segStart,
                pathEnd: isLast ? pathEnd : segEnd,
            }));
            segStart = segEnd;
            segEnd = this.variable();
        }
        return result;
    }
    visitZeroOrMorePath(path, { pathStart, pathEnd = this.variable() }) {
        const inner = this.greedyPath(path, { pathStart, pathEnd });
        return {
            whereClause: [{
                    type: 'union',
                    patterns: [{
                            type: 'bind',
                            variable: pathEnd,
                            expression: pathStart,
                        }, {
                            type: 'group',
                            patterns: inner.whereClause,
                        }],
                }],
            constructClause: inner.constructClause,
        };
    }
    visitZeroOrOnePath({ path }, { pathStart, pathEnd = this.variable() }) {
        const orMorePathVariable = this.variable();
        const inner = path.accept(this, { pathStart, pathEnd: orMorePathVariable });
        const whereClause = [{
                type: 'union',
                patterns: [{
                        type: 'bind', expression: pathStart, variable: pathEnd,
                    }, {
                        type: 'group',
                        patterns: [
                            ...inner.whereClause,
                            { type: 'bind', expression: orMorePathVariable, variable: pathEnd },
                        ],
                    }],
            }];
        return {
            whereClause,
            constructClause: inner.constructClause,
        };
    }
    greedyPath({ path }, { pathStart, pathEnd = this.variable() }) {
        if (!(path instanceof Path.PredicatePath)) {
            throw new Error('Only Predicate Path is supported as child of *OrMorePaths');
        }
        const intermediateNode = this.variable();
        return {
            whereClause: [{
                    type: 'bgp',
                    triples: [{
                            subject: pathStart,
                            predicate: {
                                type: 'path',
                                pathType: '*',
                                items: [path.term],
                            },
                            object: intermediateNode,
                        }, {
                            subject: intermediateNode,
                            predicate: path.term,
                            object: pathEnd,
                        }],
                }],
            // whereClause: sparql`${pathStart} ${path.term}* ${intermediateNode} . \n${outPattern}`,
            constructClause: [$rdf.quad(intermediateNode, path.term, pathEnd)],
        };
    }
}
